import { useState, useEffect } from 'react';
import { Container, Table, Button, Modal, Form, Alert, Badge, Spinner } from 'react-bootstrap';
import { Plus, Trash, PencilSquare, Eye, XCircle } from 'react-bootstrap-icons';
import { 
  CognitoUser, 
  CreateUserRequest, 
  UpdateUserRequest, 
  UserRole,
  Organization,
  UserActivity 
} from '../../types/admin';
import { 
  fetchCognitoUsers, 
  createCognitoUser, 
  updateCognitoUser, 
  deleteCognitoUser,
  fetchUserActivity 
} from '../../api/admin';
import { fetchOrganizations } from '../../api/admin';
import { useAuth } from 'react-oidc-context';
import { useAdmin } from '../../contexts/AdminContext';
import { formatDateTime } from '../../utils/dateFormatters';

export default function Users() {
  const auth = useAuth();
  const { isSuperAdmin, isOrgAdmin } = useAdmin();
  const [users, setUsers] = useState<CognitoUser[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Filter state
  const [selectedOrgFilter, setSelectedOrgFilter] = useState<string>('');
  const [filteredByOrg, setFilteredByOrg] = useState<string | null>(null);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<CognitoUser | null>(null);
  const [userActivity, setUserActivity] = useState<UserActivity[]>([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  
  // Form states
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserOrg, setNewUserOrg] = useState('');
  const [newUserRole, setNewUserRole] = useState<UserRole>('User');
  const [sendInvite, setSendInvite] = useState(true);
  
  const [editUserOrg, setEditUserOrg] = useState('');
  const [editUserRole, setEditUserRole] = useState<UserRole>('User');
  const [editUserEnabled, setEditUserEnabled] = useState(true);
  
  const [submitting, setSubmitting] = useState(false);

  const ACTIVITY_PAGE_SIZE = 25;
  const [activityPage, setActivityPage] = useState(0);

  const loadOrganizations = async () => {
    try {
      const response = await fetchOrganizations(auth.user?.id_token);
      setOrganizations(response.data.organizations.filter(org => org.status === 'active'));
    } catch (err: any) {
      console.error('Error loading organizations:', err);
    }
  };

  const loadUsers = async (organizationId?: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchCognitoUsers(organizationId, auth.user?.id_token);
      setUsers(response.data.users);
      setFilteredByOrg(response.data.filtered_by_org || null);
    } catch (err: any) {
      console.error('Error loading users:', err);
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth.user?.id_token) {
      loadOrganizations();
      // If OrgAdmin, only show their org's users
      if (isOrgAdmin && !isSuperAdmin) {
        // Get org from user's token
        const userOrg = auth.user.profile?.website;
        if (userOrg) {
          setSelectedOrgFilter(userOrg);
          loadUsers(userOrg);
        }
      } else {
        loadUsers();
      }
    }
  }, [auth.user?.id_token, isOrgAdmin, isSuperAdmin]);

  const handleFilterChange = (orgId: string) => {
    setSelectedOrgFilter(orgId);
    if (orgId) {
      loadUsers(orgId);
    } else {
      loadUsers();
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newUserEmail.trim()) {
      setError('Email is required');
      return;
    }

    if (!newUserOrg) {
      setError('Organization is required');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      const data: CreateUserRequest = {
        email: newUserEmail.trim(),
        organization_id: newUserOrg,
        role: newUserRole,
        send_invite: sendInvite
      };
      
      await createCognitoUser(data, auth.user?.id_token);
      
      setSuccess(`User "${newUserEmail}" created successfully${sendInvite ? ' and invite email sent' : ''}`);
      setShowCreateModal(false);
      setNewUserEmail('');
      setNewUserOrg('');
      setNewUserRole('User');
      setSendInvite(true);
      loadUsers(selectedOrgFilter || undefined);
      loadOrganizations(); // Refresh org user counts
      
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      console.error('Error creating user:', err);
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const initiateEdit = (user: CognitoUser) => {
    setSelectedUser(user);
    setEditUserOrg(user.organization_id);
    setEditUserRole(
      user.groups.includes('SuperAdmins') ? 'SuperAdmin' : 
      user.groups.includes('OrgAdmins') ? 'OrgAdmin' : 
      user.groups.includes('Attorneys') ? 'Attorney' : 'User'
    );
    setEditUserEnabled(user.enabled);
    setShowEditModal(true);
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    try {
      setSubmitting(true);
      setError(null);
      
      const currentRole = selectedUser.groups.includes('SuperAdmins') ? 'SuperAdmin' : 
                          selectedUser.groups.includes('OrgAdmins') ? 'OrgAdmin' : 
                          selectedUser.groups.includes('Attorneys') ? 'Attorney' : 'User';
      
      const data: UpdateUserRequest = {
        organization_id: editUserOrg !== selectedUser.organization_id ? editUserOrg : undefined,
        role: editUserRole !== currentRole ? editUserRole : undefined,
        enabled: editUserEnabled !== selectedUser.enabled ? editUserEnabled : undefined
      };
      
      // Only send fields that changed
      const changes: UpdateUserRequest = {};
      if (data.organization_id) changes.organization_id = data.organization_id;
      if (data.role) changes.role = data.role;
      if (data.enabled !== undefined) changes.enabled = data.enabled;
      
      await updateCognitoUser(selectedUser.user_id, changes, auth.user?.id_token);
      
      setSuccess(`User "${selectedUser.email}" updated successfully`);
      setShowEditModal(false);
      setSelectedUser(null);
      loadUsers(selectedOrgFilter || undefined);
      loadOrganizations(); // Refresh org user counts
      
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      console.error('Error updating user:', err);
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const initiateDelete = (user: CognitoUser) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      setSubmitting(true);
      setError(null);
      
      await deleteCognitoUser(selectedUser.user_id, auth.user?.id_token);
      
      setSuccess(`User "${selectedUser.email}" deleted successfully`);
      setShowDeleteModal(false);
      setSelectedUser(null);
      loadUsers(selectedOrgFilter || undefined);
      loadOrganizations(); // Refresh org user counts
      
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      console.error('Error deleting user:', err);
      const errorMsg = err.response?.data?.error || 'Failed to delete user';
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewActivity = async (user: CognitoUser) => {
    setSelectedUser(user);
    setShowActivityModal(true);
    setLoadingActivity(true);
    setUserActivity([]);
    setActivityPage(0);
    
    try {
      const response = await fetchUserActivity(user.user_id, auth.user?.id_token);
      setUserActivity(response.data.activities || []);
    } catch (err: any) {
      console.error('Error loading user activity:', err);
      setError(err.response?.data?.error || 'Failed to load user activity');
    } finally {
      setLoadingActivity(false);
    }
  };

  const getRoleBadge = (groups: string[]) => {
    if (groups.includes('SuperAdmins')) {
      return <Badge bg="danger">Super Admin</Badge>;
    } else if (groups.includes('OrgAdmins')) {
      return <Badge bg="warning" text="dark">Org Admin</Badge>;
    } else if (groups.includes('Attorneys')) {
      return <Badge bg="info">Attorney</Badge>;
    } else {
      return <Badge bg="secondary">User</Badge>;
    }
  };

  const getStatusBadge = (user: CognitoUser) => {
    if (!user.enabled) {
      return <Badge bg="secondary">Disabled</Badge>;
    }
    switch (user.user_status) {
      case 'CONFIRMED':
        return <Badge bg="success">Active</Badge>;
      case 'UNCONFIRMED':
        return <Badge bg="warning" text="dark">Unconfirmed</Badge>;
      case 'FORCE_CHANGE_PASSWORD':
        return <Badge bg="info">Password Reset</Badge>;
      default:
        return <Badge bg="secondary">{user.user_status}</Badge>;
    }
  };

  const canEditUser = (user: CognitoUser) => {
    if (isSuperAdmin) return true;
    if (isOrgAdmin) {
      // OrgAdmins can only edit users in their org
      const userOrg = auth.user?.profile?.website;
      return user.organization_id === userOrg;
    }
    return false;
  };

  const canDeleteUser = (user: CognitoUser) => {
    if (isSuperAdmin) return true;
    if (isOrgAdmin) {
      // OrgAdmins can only delete users in their org (not other admins)
      const userOrg = auth.user?.profile?.website;
      return user.organization_id === userOrg && !user.groups.includes('SuperAdmins');
    }
    return false;
  };

  return (
    <Container className="my-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>User Management</h2>
          <p className="text-muted">Manage users and their access</p>
        </div>
        {(isSuperAdmin || isOrgAdmin) && (
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            <Plus className="me-2" />
            Add User
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Organization Filter */}
      {isSuperAdmin && organizations.length > 0 && (
        <div className="mb-3">
          <Form.Select
            value={selectedOrgFilter}
            onChange={(e) => handleFilterChange(e.target.value)}
            style={{ maxWidth: '300px' }}
          >
            <option value="">All Organizations</option>
            {organizations.map(org => (
              <option key={org.organization_id} value={org.organization_id}>
                {org.organization_name}
              </option>
            ))}
          </Form.Select>
          {filteredByOrg && (
            <small className="text-muted ms-2">
              Showing users from: {organizations.find(o => o.organization_id === filteredByOrg)?.organization_name || filteredByOrg}
            </small>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-2">Loading users...</p>
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Email</th>
              <th>Organization</th>
              <th>Role</th>
              <th>Status</th>
              <th>Created</th>
              {(isSuperAdmin || isOrgAdmin) && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.user_id}>
                <td>
                  <strong>{user.email}</strong>
                  {!user.email_verified && (
                    <Badge bg="warning" text="dark" className="ms-2">Unverified</Badge>
                  )}
                </td>
                <td>{user.organization_name}</td>
                <td>{getRoleBadge(user.groups)}</td>
                <td>{getStatusBadge(user)}</td>
                <td>{formatDateTime(user.created_date)}</td>
                {(isSuperAdmin || isOrgAdmin) && (
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => handleViewActivity(user)}
                        title="View activity"
                      >
                        <Eye />
                      </Button>
                      {canEditUser(user) && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => initiateEdit(user)}
                          title="Edit user"
                        >
                          <PencilSquare />
                        </Button>
                      )}
                      {canDeleteUser(user) && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => initiateDelete(user)}
                          title="Delete user"
                        >
                          <Trash />
                        </Button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={(isSuperAdmin || isOrgAdmin) ? 6 : 5} className="text-center">
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      {/* Create User Modal */}
      <Modal show={showCreateModal} onHide={() => !submitting && setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New User</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateUser}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Email Address *</Form.Label>
              <Form.Control
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="user@example.com"
                required
                disabled={submitting}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Organization *</Form.Label>
              <Form.Select
                value={newUserOrg}
                onChange={(e) => setNewUserOrg(e.target.value)}
                required
                disabled={submitting}
              >
                <option value="">Select organization...</option>
                {organizations.map(org => (
                  <option key={org.organization_id} value={org.organization_id}>
                    {org.organization_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Role *</Form.Label>
              <Form.Select
                value={newUserRole}
                onChange={(e) => setNewUserRole(e.target.value as UserRole)}
                required
                disabled={submitting || !isSuperAdmin}
              >
                <option value="User">User (Full Access)</option>
                <option value="Attorney">Attorney / Paralegal (Limited Access)</option>
                {isSuperAdmin && (
                  <>
                    <option value="OrgAdmin">Org Admin</option>
                    <option value="SuperAdmin">Super Admin</option>
                  </>
                )}
              </Form.Select>
              <Form.Text className="text-muted">
                {!isSuperAdmin 
                  ? 'Only Super Admins can assign admin roles'
                  : 'Attorneys/Paralegals can only upload files and manage requirements on cases they are assigned to'}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Send invitation email"
                checked={sendInvite}
                onChange={(e) => setSendInvite(e.target.checked)}
                disabled={submitting}
              />
              <Form.Text className="text-muted">
                If checked, user will receive an email with temporary password to set up their account
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create User'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit User Modal */}
      <Modal show={showEditModal} onHide={() => !submitting && setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit User: {selectedUser?.email}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditUser}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="text"
                value={selectedUser?.email || ''}
                disabled
                readOnly
              />
              <Form.Text className="text-muted">Email cannot be changed</Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Organization</Form.Label>
              <Form.Select
                value={editUserOrg}
                onChange={(e) => setEditUserOrg(e.target.value)}
                disabled={submitting || !isSuperAdmin}
              >
                {organizations.map(org => (
                  <option key={org.organization_id} value={org.organization_id}>
                    {org.organization_name}
                  </option>
                ))}
              </Form.Select>
              {!isSuperAdmin && (
                <Form.Text className="text-muted">
                  Only Super Admins can change organization
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={editUserRole}
                onChange={(e) => setEditUserRole(e.target.value as UserRole)}
                disabled={submitting || !isSuperAdmin}
              >
                <option value="User">User (Full Access)</option>
                <option value="Attorney">Attorney / Paralegal (Limited Access)</option>
                {isSuperAdmin && (
                  <>
                    <option value="OrgAdmin">Org Admin</option>
                    <option value="SuperAdmin">Super Admin</option>
                  </>
                )}
              </Form.Select>
              <Form.Text className="text-muted">
                {!isSuperAdmin 
                  ? 'Only Super Admins can change roles'
                  : 'Attorneys/Paralegals can only upload files and manage requirements'}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="User Enabled"
                checked={editUserEnabled}
                onChange={(e) => setEditUserEnabled(e.target.checked)}
                disabled={submitting}
              />
              <Form.Text className="text-muted">
                Disabled users cannot log in
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete User Modal */}
      <Modal show={showDeleteModal} onHide={() => !submitting && setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete the user:</p>
          <p className="fw-bold">{selectedUser?.email}</p>
          
          {error && (
            <Alert variant="danger" className="mt-3">
              {error}
            </Alert>
          )}
          
          <Alert variant="warning" className="mt-3">
            <strong>Warning:</strong> This will immediately revoke all sessions and prevent the user from logging in. This action cannot be undone.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteUser} disabled={submitting}>
            {submitting ? 'Deleting...' : 'Delete User'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* User Activity Modal */}
      <Modal show={showActivityModal} onHide={() => setShowActivityModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Activity: {selectedUser?.email}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingActivity ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading activity...</p>
            </div>
          ) : userActivity.length === 0 ? (
            <Alert variant="info">No activity found for this user</Alert>
          ) : (() => {
            const sorted = [...userActivity].sort((a, b) => b.timestamp.localeCompare(a.timestamp));
            const totalPages = Math.ceil(sorted.length / ACTIVITY_PAGE_SIZE);
            const pageItems = sorted.slice(activityPage * ACTIVITY_PAGE_SIZE, (activityPage + 1) * ACTIVITY_PAGE_SIZE);
            return (
              <>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <small className="text-muted">
                    {sorted.length} total entries — showing {activityPage * ACTIVITY_PAGE_SIZE + 1}–{Math.min((activityPage + 1) * ACTIVITY_PAGE_SIZE, sorted.length)}
                  </small>
                </div>
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th>Action</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((activity) => (
                      <tr key={activity.audit_id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(activity.timestamp)}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{activity.action}</td>
                        <td>{activity.details || <span className="text-muted">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center align-items-center gap-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setActivityPage(p => p - 1)}
                      disabled={activityPage === 0}
                    >
                      ← Prev
                    </Button>
                    <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                      Page {activityPage + 1} of {totalPages}
                    </span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setActivityPage(p => p + 1)}
                      disabled={activityPage >= totalPages - 1}
                    >
                      Next →
                    </Button>
                  </div>
                )}
              </>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowActivityModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

